import { _ } from "../../../../core/polyglot/polyglot.js";
import AbstractColumnTemplate from "./AbstractColumnTemplate.js";
import DeviationColumn from "../../reports/columns/DeviationColumn.js";

/**
 * Deviation column template.
 *
 * @param {string} spec.baseValuesColumnTemplateId - id of column template which, after interpreting, will yield the column to use as base values.
 * @param {string} spec.deviatedValuesColumnTemplateId - id of column template which, after interpreting, will yield the column to use as deviated values.
 * @param {boolean} [spec.inPercent=false] - flag indicating whether deviation is calculated as percentage.
 */
export default class DeviationColumnTemplate extends AbstractColumnTemplate {
	constructor({
		baseValuesColumnTemplateId,
		deviatedValuesColumnTemplateId,
		inPercent = false,
	}) {
		super(...arguments);

		this._baseValuesColumnTemplateId = baseValuesColumnTemplateId;
		this._deviatedValuesColumnTemplateId = deviatedValuesColumnTemplateId;
		this._inPercent = inPercent;
	}

	getBaseValuesColumnTemplate() {
		return this._reportTemplate.findColumnRecursively({
			predicate: (column) =>
				column.getId() === this._baseValuesColumnTemplateId,
		});
	}

	setBaseValuesColumnTemplate(column) {
		this._baseValuesColumnTemplateId = column.getId();
	}

	getDeviatedValuesColumnTemplate() {
		return this._reportTemplate.findColumnRecursively({
			predicate: (column) =>
				column.getId() === this._deviatedValuesColumnTemplateId,
		});
	}

	setDeviatedValuesColumnTemplate(column) {
		this._deviatedValuesColumnTemplateId = column.getId();
	}

	hasPercentageFormat() {
		return this._inPercent;
	}

	setInPercent(value) {
		this._inPercent = value;
	}

	validate({ collector }) {
		super.validate(...arguments);

		collector.addErrorIf(
			!this.getBaseValuesColumnTemplate(),
			_("Deviation column with no base column"),
		);

		collector.addErrorIf(
			!this.getDeviatedValuesColumnTemplate(),
			_("Deviation column with no deviated column"),
		);
	}

	accept(visitor) {
		return visitor.visitDeviationColumnTemplate(this);
	}

	_instanceClass() {
		return DeviationColumn;
	}

	_interpretationSpec(interpreter) {
		const spec = super._interpretationSpec(...arguments);

		const baseValuesColumnTemplate = this.getBaseValuesColumnTemplate();
		const baseValuesColumn =
			baseValuesColumnTemplate &&
			interpreter.interpretItemTemplate(baseValuesColumnTemplate);

		const deviatedValuesColumnTemplate =
			this.getDeviatedValuesColumnTemplate();
		const deviatedValuesColumn =
			deviatedValuesColumnTemplate &&
			interpreter.interpretItemTemplate(deviatedValuesColumnTemplate);

		spec.baseValuesColumn = baseValuesColumn;
		spec.deviatedValuesColumn = deviatedValuesColumn;
		spec.inPercent = this._inPercent;

		return spec;
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.baseValuesColumnTemplateId =
			this._baseValuesColumnTemplateId;

		properties.deviatedValuesColumnTemplateId =
			this._deviatedValuesColumnTemplateId;

		properties.inPercent = this._inPercent;

		return properties;
	}

	static getLabel() {
		return _("Deviation");
	}

	static getType() {
		return "DeviationColumnTemplate";
	}
}
